import { Skeleton } from "@kobalte/core/skeleton";
import { useNavigate, useSearchParams } from "@solidjs/router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/solid-query";
import { IoFilter, IoPersonOutline } from "solid-icons/io";
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { JOB_OFFER_KEY } from "@/api/query-keys";
import { Container } from "@/components/shared/Container";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import type { JobApplication } from "@/domains/application/types";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { usePostHog } from "@/hooks/usePostHog";
import { parseInputValue } from "@/libs/format";
import { getJobsOffers, useGetJobOfferV2 } from "../api";
import { useFilterBadges, useFilterStore } from "../context/FilterProvider";
import { useJobListContext } from "../context/JobListContext";
import type { JobOfferV2WithCompanyId } from "../types";
import { AdvancedFiltersSheet } from "./AdvancedFiltersSheet";
import { DetailSheet } from "./detail-sheet";
import { JobCard } from "./job-card";
import { SortSelector } from "./SearchBar";

const JobListSkeleton: Component = () => {
  return (
    <div class="flex flex-col gap-5 w-full max-w-4xl mx-auto">
      <Skeleton
        class="w-full h-24 rounded-sm animate-pulse bg-gray-200"
        radius={4}
        height={160}
      />
      <Skeleton
        class="w-full h-24 rounded-sm animate-pulse bg-gray-200"
        radius={4}
        height={160}
      />
      <Skeleton
        class="w-full h-24 rounded-sm animate-pulse bg-gray-200"
        radius={4}
        height={160}
      />
      <Skeleton
        class="w-full h-24 rounded-sm animate-pulse bg-gray-200"
        radius={4}
        height={160}
      />
    </div>
  );
};

export const JobList: Component<{ userApplications: JobApplication[] }> = (
  props,
) => {
  const { capture } = usePostHog();
  const [filterStore, setFilterStore] = useFilterStore();
  const [authStore] = useAuthV2();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const jobsQuery = useInfiniteQuery(() => ({
    queryKey: [JOB_OFFER_KEY, "all", filterStore],
    queryFn: ({ pageParam = 1 }) =>
      getJobsOffers({
        page: pageParam,
        skills: filterStore.skills ?? undefined,
        daily_rate_min:
          filterStore.daily_rate_min &&
          parseInputValue(filterStore.daily_rate_min) !== ""
            ? parseInputValue(filterStore.daily_rate_min)
            : undefined,
        daily_rate_max:
          filterStore.daily_rate_max &&
          parseInputValue(filterStore.daily_rate_max) !== ""
            ? parseInputValue(filterStore.daily_rate_max)
            : undefined,
        experience_level: filterStore.experience_level ?? undefined,
        job_category: filterStore.job_category ?? undefined,
        project_type: filterStore.project_type ?? undefined,
        effort: filterStore.effort ?? undefined,
        duration: filterStore.duration ?? undefined,
        payment_type: filterStore.payment_type ?? undefined,
        budget:
          filterStore.budget && parseInputValue(filterStore.budget) !== ""
            ? parseInputValue(filterStore.budget)
            : undefined,
        sortBy: filterStore.sortBy,
        sortOrder: filterStore.sortOrder,
        search: filterStore.search ?? undefined,
        payment_status: "completed",
        remote_policy: filterStore.remote_policy ?? undefined,
        start_date: filterStore.start_date ?? undefined,
        renewable: filterStore.renewable ?? undefined,
        for_me: filterStore.for_me ?? undefined,
      }),
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => firstPage.pagination.prevPage,
    getNextPageParam: (lastPage) => lastPage.pagination.nextPage,
  }));

  const defaultJobQuery = useGetJobOfferV2(
    searchParams.job as string,
    "completed",
  );

  const offers = createMemo(() => {
    const allJobs = jobsQuery.data?.pages.flatMap((p) => p.data) ?? [];
    const defaultJobId = defaultJobQuery.data?.job_offer.id;

    const uniqueJobs = new Map<string, JobOfferV2WithCompanyId>();

    allJobs.forEach((job) => {
      if (job.id !== defaultJobId && !uniqueJobs.has(job.id)) {
        uniqueJobs.set(job.id, job);
      }
    });

    return Array.from(uniqueJobs.values());
  });

  createEffect(() => {
    const sortBy = filterStore.sortBy;
    const sortOrder = filterStore.sortOrder;
    if (sortBy && sortOrder) {
      queryClient.resetQueries({
        queryKey: [JOB_OFFER_KEY, "all", filterStore],
      });
    }
  });

  const { t } = useI18n();

  const [isFiltersOpen, setIsFiltersOpen] = createSignal(false);

  const filterBadges = useFilterBadges();

  const { handleJobCardClick, selectedJob } = useJobListContext();

  const handleLoadMoreJobs = () => {
    if (jobsQuery.hasNextPage) {
      capture("click_load_more_jobs_offers");
      jobsQuery.fetchNextPage();
    }
  };

  const handleForMeClick = () => {
    // Check if user is authenticated
    if (!authStore?.profile?.id) {
      // Redirect to auth if not logged in
      navigate("/auth");
      return;
    }

    // Toggle for_me filter
    const newForMeValue = filterStore.for_me ? undefined : true;
    setFilterStore("for_me", newForMeValue);
    if (newForMeValue) {
      capture("click_for_me_filter");
    }
  };

  return (
    <Show when={jobsQuery.isSuccess} fallback={<JobListSkeleton />}>
      <div class="flex flex-col gap-2 w-full">
        <Show when={defaultJobQuery.isSuccess && defaultJobQuery.data}>
          <JobCard
            job={defaultJobQuery.data!.job_offer}
            isAlreadyApplied={props.userApplications.some(
              (application) =>
                application.job_offer_id === defaultJobQuery.data?.job_offer.id,
            )}
            onClick={() =>
              handleJobCardClick(
                defaultJobQuery.data!.job_offer,
                props.userApplications.some(
                  (application) =>
                    application.job_offer_id ===
                    defaultJobQuery.data?.job_offer.id,
                ),
              )
            }
          />
          <div class="my-4 w-full">
            <p class="text-lg font-semibold">{t("joblist.title")}</p>
          </div>
        </Show>
        <Show when={defaultJobQuery.isError}>
          <Alert variant="warning">
            <AlertTitle>{t("joblist.noResults")}</AlertTitle>
            <AlertDescription>
              {t("joblist.noResultsDescription")}
            </AlertDescription>
          </Alert>
        </Show>
        <div class="flex items-center justify-between w-full flex-wrap gap-2 md:gap-0">
          <p class="text-sm text-gray-500">
            {t("joblist.total", {
              count: jobsQuery.data?.pages?.[0]?.pagination?.total ?? 0,
            })}
          </p>
          <div class="flex items-center gap-2 w-full md:w-auto">
            <Show when={authStore?.profile?.id}>
              <Button
                variant={filterStore.for_me ? "default" : "outline"}
                class="w-full md:w-auto"
                size="default"
                onClick={handleForMeClick}
              >
                <IoPersonOutline size={16} />
                <span
                  class="text-xs ml-1"
                  classList={{
                    "text-gray-500": !filterStore.for_me,
                    "text-white": filterStore.for_me,
                  }}
                >
                  {t("home.search.actions.forMe")}
                </span>
              </Button>
            </Show>
            <Button
              variant={"outline"}
              class="w-full md:w-auto"
              size="default"
              onClick={() => setIsFiltersOpen(!isFiltersOpen())}
            >
              <IoFilter size={16} />
              <span class="text-xs text-gray-500 ml-1">
                {t("home.search.actions.advancedFilters")}
              </span>
              <Show when={filterBadges().length > 0}>
                <span class="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 ml-1">
                  {filterBadges().length}
                </span>
              </Show>
            </Button>

            <Show when={isFiltersOpen()}>
              <AdvancedFiltersSheet
                isOpen={isFiltersOpen()}
                onOpenChange={setIsFiltersOpen}
              />
            </Show>
            <SortSelector />
          </div>
        </div>
        <div class="flex flex-col gap-4">
          <For each={offers()}>
            {(job) => {
              const isAlreadyApplied = props.userApplications.some(
                (application) => application.job_offer_id === job.id,
              );

              return (
                <JobCard
                  job={job}
                  isAlreadyApplied={isAlreadyApplied}
                  onClick={() => handleJobCardClick(job, isAlreadyApplied)}
                />
              );
            }}
          </For>
        </div>
      </div>
      <Show when={selectedJob()}>
        <DetailSheet />
      </Show>
      <Show when={jobsQuery.isFetchingNextPage}>
        <Loading />
      </Show>
      <Show when={!jobsQuery.isFetching}>
        <Show
          when={jobsQuery.hasNextPage}
          fallback={
            <Container
              size={"4xl"}
              class="mx-auto flex flex-col items-center justify-center h-24 w-full border border-gray-300 rounded-sm mt-4 mb-4"
            >
              <p class="text-sm text-gray-500">{t("joblist.noMoreJobs")}</p>
              <Show when={authStore?.capabilities?.can_publish}>
                <Button
                  variant="outline"
                  class="mt-4"
                  size="sm"
                  as="a"
                  href="/job-offers/create/ai"
                >
                  {t("joblist.publishJob")}
                </Button>
              </Show>
            </Container>
          }
        >
          <Container
            size={"4xl"}
            class="mx-auto flex flex-col items-center justify-center h-24 border-t border-gray-300 mt-4 mb-4"
          >
            <Button
              variant="outline"
              size="lg"
              onClick={handleLoadMoreJobs}
              disabled={jobsQuery.isFetchingNextPage}
            >
              {t("joblist.show_more_jobs")}
            </Button>
          </Container>
        </Show>
      </Show>
    </Show>
  );
};
